import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  documentId,
  orderBy,
  limit,
} from 'firebase/firestore'
import type { QueryDocumentSnapshot, DocumentData } from 'firebase/firestore'
import { db } from '@/lib/firebase'
import type { Member } from '@/types/models'
import { docData } from '@/utils/firestore'
import { getPaginatedCollection } from '@/utils/paginatedQuery'

const COLLECTION = 'members'
export const MEMBERS_PAGE_SIZE = 30

export interface MembersPage {
  members: Member[]
  lastDoc: QueryDocumentSnapshot<DocumentData> | null
}

export async function getMembers(churchId: string): Promise<Member[]> {
  const q = query(collection(db, COLLECTION), where('churchId', '==', churchId))
  const snapshot = await getDocs(q)
  return snapshot.docs.map((d) => docData<Member>(d))
}

export async function getMember(id: string): Promise<Member> {
  const snap = await getDoc(doc(db, COLLECTION, id))
  if (!snap.exists()) throw new Error('Member not found')
  return docData<Member>(snap)
}

export async function addMember(
  data: Omit<Member, 'id' | 'nameLower'>,
): Promise<string> {
  const ref = await addDoc(collection(db, COLLECTION), {
    ...data,
    nameLower: data.name.toLowerCase(),
  })
  return ref.id
}

export async function updateMember(
  id: string,
  data: Partial<Omit<Member, 'id'>>,
): Promise<void> {
  const { additionalDetails, ...rest } = data
  const update: Record<string, unknown> = { ...rest }
  if (data.name !== undefined) {
    update.nameLower = data.name.toLowerCase()
  }
  // Expand additionalDetails into dotted field paths so Firestore merges
  // into the nested map instead of replacing it wholesale. Otherwise any
  // field not in the form (e.g. PCO-synced `campus`, `membershipType`)
  // gets wiped on every edit until the next external sync.
  if (additionalDetails) {
    for (const [key, value] of Object.entries(additionalDetails)) {
      update[`additionalDetails.${key}`] = value
    }
  }
  await updateDoc(doc(db, COLLECTION, id), update)
}

export async function deleteMember(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTION, id))
}

export async function getMembersPaginated(
  churchId: string,
  pageParam: QueryDocumentSnapshot<DocumentData> | null,
): Promise<MembersPage> {
  // TODO: remove - debug
  console.log(
    '[getMembersPaginated] called with churchId:',
    churchId,
    'pageParam:',
    pageParam,
  )
  const { items, lastDoc } = await getPaginatedCollection({
    collectionName: COLLECTION,
    churchId,
    pageParam,
    orderField: 'name',
    pageSize: MEMBERS_PAGE_SIZE,
    mapper: (d) => docData<Member>(d),
  })
  console.log('[getMembersPaginated] returned', items.length, 'members')
  return { members: items, lastDoc }
}

export async function getMembersByIds(ids: string[]): Promise<Member[]> {
  if (ids.length === 0) return []
  // Firestore 'in' queries are limited to 30 items
  const batches: string[][] = []
  for (let i = 0; i < ids.length; i += 30) {
    batches.push(ids.slice(i, i + 30))
  }
  const results: Member[] = []
  for (const batch of batches) {
    const q = query(
      collection(db, COLLECTION),
      where(documentId(), 'in', batch),
    )
    const snapshot = await getDocs(q)
    results.push(...snapshot.docs.map((d) => docData<Member>(d)))
  }
  return results
}

export async function searchMembers(
  churchId: string,
  prefix: string,
): Promise<Member[]> {
  if (!prefix.trim()) return []
  const normalizedPrefix = prefix.trim().toLowerCase()
  const q = query(
    collection(db, COLLECTION),
    where('churchId', '==', churchId),
    where('nameLower', '>=', normalizedPrefix),
    where('nameLower', '<', normalizedPrefix + '\uf8ff'),
    orderBy('nameLower'),
    limit(20),
  )
  const snapshot = await getDocs(q)
  return snapshot.docs.map((d) => docData<Member>(d))
}
